/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Undo2, Redo2, Check, AlertTriangle, History, X } from 'lucide-react';
import { ChangementPlan } from './types.ts';

interface BandeauBrouillonProps {
  changements: ChangementPlan[];
  peutDefaire: boolean;
  peutRefaire: boolean;
  onAnnulerDernier: () => void;
  onRefaire: () => void;
  onOuvrirModalApplication: () => void;
  onReinitialiserBrouillon: () => void;
  onOuvrirJournal: () => void;
}

export const BandeauBrouillon: React.FC<BandeauBrouillonProps> = ({
  changements,
  peutDefaire,
  peutRefaire,
  onAnnulerDernier,
  onRefaire,
  onOuvrirModalApplication,
  onReinitialiserBrouillon,
  onOuvrirJournal,
}) => {
  if (changements.length === 0) return null;

  return (
    <aside
      className="bg-amber-500 text-slate-950 px-3 sm:px-4 py-2 sm:py-2.5 flex flex-wrap items-center justify-between gap-2 text-xs sm:text-sm font-medium shadow-md transition-all select-none border-b border-amber-600"
      aria-live="assertive"
      aria-label="État du brouillon en cours"
    >
      {/* Information brouillon */}
      <div className="flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full bg-slate-950 animate-ping" aria-hidden="true" />
        <span className="font-bold tracking-tight">
          Brouillon en direct : {changements.length} modification{changements.length > 1 ? 's' : ''}{' '}
          non appliquée{changements.length > 1 ? 's' : ''}
        </span>
        <span className="text-[11px] bg-amber-400 text-slate-900 px-2 py-0.5 rounded font-mono hidden md:inline">
          (Raccourcis : Ctrl+Z / Ctrl+Maj+Z)
        </span>
      </div>

      {/* Actions de brouillon : Annuler, Refaire, Journal, Appliquer */}
      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
        <button
          type="button"
          onClick={onAnnulerDernier}
          disabled={!peutDefaire}
          aria-label="Annuler le dernier changement"
          className="px-2.5 py-1.5 rounded bg-amber-400 hover:bg-amber-300 active:scale-95 text-slate-950 font-semibold flex items-center gap-1 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed min-h-[36px] sm:min-h-[40px] text-xs transition-colors"
        >
          <Undo2 className="w-4 h-4" />
          <span>Annuler le dernier changement</span>
        </button>

        {peutRefaire && (
          <button
            type="button"
            onClick={onRefaire}
            aria-label="Rétablir le changement"
            className="p-2 rounded bg-amber-400 hover:bg-amber-300 text-slate-950 cursor-pointer min-h-[36px] sm:min-h-[40px] min-w-[36px] flex items-center justify-center transition-colors"
            title="Rétablir (Ctrl+Maj+Z)"
          >
            <Redo2 className="w-4 h-4" />
          </button>
        )}

        <button
          type="button"
          onClick={onOuvrirJournal}
          aria-label="Afficher le journal des changements"
          className="px-2 py-1.5 rounded bg-amber-400 hover:bg-amber-300 text-slate-950 font-semibold flex items-center gap-1 cursor-pointer min-h-[36px] sm:min-h-[40px] text-xs transition-colors"
        >
          <History className="w-4 h-4" />
          <span className="hidden sm:inline">Journal</span>
        </button>

        <button
          type="button"
          onClick={onReinitialiserBrouillon}
          aria-label="Abandonner toutes les modifications du brouillon"
          className="p-1.5 rounded text-amber-900 hover:bg-amber-400/50 cursor-pointer min-h-[36px] min-w-[36px] flex items-center justify-center"
          title="Abandonner le brouillon"
        >
          <X className="w-4 h-4" />
        </button>

        <button
          type="button"
          onClick={onOuvrirModalApplication}
          aria-label="Appliquer les changements sur le plan"
          className="px-3.5 py-1.5 rounded bg-slate-950 text-white hover:bg-slate-900 active:scale-95 font-bold flex items-center gap-1.5 cursor-pointer min-h-[40px] sm:min-h-[42px] text-xs sm:text-sm shadow-md transition-all ml-1"
        >
          <Check className="w-4 h-4 text-emerald-400" />
          <span>Appliquer les changements</span>
        </button>
      </div>
    </aside>
  );
};
